import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Paper,
} from "@mui/material";
import { useAuth } from "../../hooks/useAuth";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { useStudents } from "../../hooks/useStudents";
import { useSubjects, useVocationalSubjects } from "../../hooks/useSubjects";
import { useMarksheetTypeOptions, useMonthTypeOptions, shouldUseNAForMonthType } from "../enum/MarksheetTypeOptions";
import { SelectChangeEvent } from "@mui/material/Select";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SubjectIcon from "@mui/icons-material/Subject";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTranslation } from "react-i18next";
import { useError } from "../../contexts/ErrorContext";
import { MarksheetEntryDto } from "../../hooks/useMarkSheets";

export interface StudentMarkInput {
  studentId: number;
  studentName: string;
  rollNumber: string;
  theoryMarks: number | string | null;
  practicalMarks: number | string | null;
  marksObtained: number | string | null; // For monthly marksheet
}

export interface SubjectMarksDto {
  studentId: number;
  subjectCode: string;
  theory: number;
  practical: number;
}

export interface MarksEntryFormData {
  marksheetType: string;
  monthType: string;
  classId: number;
  sessionId: number;
  subjectId: number;
  studentMarks: StudentMarkInput[];
  subjectMarksDtoList?: SubjectMarksDto[]; // Added for API submission
}

interface Props {
  open: boolean;
  handleClose: () => void;
  onSubmit: (data: MarksEntryFormData) => void;
  marksEntries?: MarksheetEntryDto[]; // Optional: existing marks entries to pre-fill
  onViewMarksEntries?: () => void; // Optional: callback to open view marks entries modal
}

// Helper function to get marksheet type from sessionStorage (used for auto-selection)
const getStoredMarksheetType = (): string => {
  if (typeof sessionStorage !== 'undefined') {
    const storedType = sessionStorage.getItem("marksheetTypeFilter");
    return storedType || "QUARTERLY"; // Default to QUARTERLY if not stored
  }
  return "QUARTERLY";
};

const MarksEntryFormModal: React.FC<Props> = ({
  open,
  handleClose,
  onSubmit,
  marksEntries = [],
  onViewMarksEntries,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isStudent = user?.roles?.includes('STUDENT');
  const { showError: showErrorToast } = useError();
  const { selectedClass, selectedSession } = useSessionClass();
  const marksheetTypeOptions = useMarksheetTypeOptions(selectedClass?.name);
  const monthTypeOptions = useMonthTypeOptions();
  const schoolId = user?.schoolId ?? 0;
  const classId = selectedClass?.id ?? 0;
  const className = selectedClass
    ? `${selectedClass.name} ${selectedClass.section}`
    : "";
  const sessionYear = selectedSession?.name ?? "";
  const selectedSessionId = selectedSession?.id;
  const { studentsQuery, useStudentsByClassId, useStudentsBySessionIdAndClassId } = useStudents();
  const { subjectsQuery } = useSubjects();
  const { data: vocationalSubjects } = useVocationalSubjects(classId);

  // Normalize IDs for the hook - ensure they are valid numbers or undefined
  const normalizedSessionId = selectedSessionId && selectedSessionId > 0 ? selectedSessionId : undefined;
  const normalizedClassId = classId && classId > 0 ? classId : undefined;

  // Use filtered API query when both session and class are selected, but disable for students
  const { 
    data: filteredStudentsFromAPI = [], 
    isLoading: isLoadingFilteredStudents 
  } = useStudentsBySessionIdAndClassId(
    isStudent ? undefined : normalizedSessionId,
    isStudent ? undefined : normalizedClassId
  );

  // Fallback to class-based query when session is not selected, but disable for students
  const studentsByClassIdQuery = useStudentsByClassId(isStudent ? 0 : classId);
  const allStudents = studentsQuery.data || [];

  // Helper function to extract base class name
  const getBaseClassName = (className: string): string => {
    if (!className) return "";
    const match = className.match(/^(\d+(?:th|st|nd|rd)?)/i);
    return match ? match[1] : className.split(/\s+/)[0];
  };

  const baseClassName = selectedClass?.name ? getBaseClassName(selectedClass.name) : "";

  // Filter subjects for current class
  const allClassSubjects = useMemo(() => {
    const filtered = (subjectsQuery.data || []).filter((s) => {
      if (!s.className) return false;
      const subjectBaseClassName = getBaseClassName(s.className);
      return subjectBaseClassName === baseClassName;
    });
    return filtered;
  }, [subjectsQuery.data, baseClassName]);

  // Helper function to get student display name
  const getStudentDisplayName = (student: any): string => {
    if (student.studentName) {
      return student.studentName;
    } else if (student.firstName && student.lastName) {
      return `${student.firstName} ${student.lastName}`;
    } else if (student.firstName) {
      return student.firstName;
    } else if (student.lastName) {
      return student.lastName;
    } else {
      return 'Unknown Student';
    }
  };

  // Helper function to safely get student class ID
  const getStudentClassId = (student: any): number => {
    return student.schoolClassId || student.classId || 0;
  };

  // Filter students by class, session, and subject enrollment
  // Use filtered API when both session and class are selected, otherwise filter manually
  const filteredStudents = useMemo(() => {
    // If both session and class are selected, use the filtered API results
    if (normalizedSessionId && normalizedClassId) {
      return (filteredStudentsFromAPI || [])
        .filter((student) => student.status !== false)
        .sort((a, b) => {
          const nameA = getStudentDisplayName(a);
          const nameB = getStudentDisplayName(b);
          return nameA.localeCompare(nameB);
        });
    }

    // Otherwise, use the old filtering logic for backward compatibility
    if (!selectedSession?.id || !classId) {
      return [];
    }

    let studentsToFilter = studentsByClassIdQuery.data || [];
    if (studentsToFilter.length === 0 && allStudents.length > 0) {
      studentsToFilter = allStudents;
    }

    // Filter by class and session
    let students = studentsToFilter.filter(student => {
      const studentClassId = getStudentClassId(student);
      const classMatch = studentClassId === classId;
      const sessionMatch = student.sessionId === selectedSession.id;
      const isActive = student.status !== false;
      return classMatch && sessionMatch && isActive;
    });

    // Sort alphabetically by name
    students.sort((a, b) => {
      const nameA = getStudentDisplayName(a);
      const nameB = getStudentDisplayName(b);
      return nameA.localeCompare(nameB);
    });

    return students;
  }, [
    normalizedSessionId, 
    normalizedClassId, 
    filteredStudentsFromAPI, 
    studentsByClassIdQuery.data, 
    allStudents, 
    selectedSession?.id, 
    classId
  ]);

  // Initial form state
  const [form, setForm] = useState<MarksEntryFormData>({
    marksheetType: getStoredMarksheetType(),
    monthType: "JANUARY",
    classId: classId,
    sessionId: selectedSessionId ?? 0,
    subjectId: 0,
    studentMarks: [],
  });

  const effectiveMarksheetType =
    form.marksheetType === "FINAL" ? "ANNUAL" : form.marksheetType;

  // Filter students enrolled in selected subject
  const studentsEnrolledInSubject = useMemo(() => {
    if (!form.subjectId || filteredStudents.length === 0) {
      return [];
    }

    const selectedSubject = allClassSubjects.find(s => s.id === form.subjectId);
    if (!selectedSubject) return [];

    return filteredStudents.filter(student => {
      let studentSubjectCodes: string[] = [];
      if (Array.isArray(student.subjects)) {
        studentSubjectCodes = student.subjects;
      } else if (typeof student.subjects === 'string') {
        studentSubjectCodes = student.subjects.split(',').map((s: string) => s.trim()).filter(Boolean);
      }
      return studentSubjectCodes.includes(selectedSubject.subjectCode);
    });
  }, [form.subjectId, filteredStudents, allClassSubjects]);

  // Form errors
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const lastSelectionKeyRef = useRef<string>("");
  const lastAutofillAppliedKeyRef = useRef<string>("");

  // Update form when modal opens
  useEffect(() => {
    if (!open) return;
    
    const marksheetType = getStoredMarksheetType();
    const monthType = shouldUseNAForMonthType(marksheetType) ? "NA" : "JANUARY";
    
    setForm({
      marksheetType: marksheetType,
      monthType: monthType,
      classId: classId,
      sessionId: selectedSessionId ?? 0,
      subjectId: 0,
      studentMarks: [],
    });
    setFormErrors({});
    // Reset refs when modal opens
    lastSelectionKeyRef.current = "";
    lastAutofillAppliedKeyRef.current = "";
  }, [open, classId, selectedSessionId]);

  const getSelectionKey = React.useCallback(() => {
    return [
      form.marksheetType,
      form.marksheetType === "MONTHLY" ? (form.monthType || "JANUARY") : "NA",
      form.classId,
      form.sessionId,
      form.subjectId,
    ].join("|");
  }, [form.marksheetType, form.monthType, form.classId, form.sessionId, form.subjectId]);

  // When selection changes, clear stale marks first.
  // Then the autofill effect (below) can re-populate if matching marks entries exist.
  useEffect(() => {
    if (!open) return;
    if (!form.subjectId) {
      setForm((prev) => ({ ...prev, studentMarks: [] }));
      return;
    }
    if (studentsEnrolledInSubject.length === 0) {
      setForm((prev) => ({ ...prev, studentMarks: [] }));
      return;
    }

    const selectionKey = getSelectionKey();
    if (lastSelectionKeyRef.current === selectionKey) return;
    lastSelectionKeyRef.current = selectionKey;
    lastAutofillAppliedKeyRef.current = "";

    // Clear marks to avoid carry-over between exam/month/subject selections.
    const cleared = studentsEnrolledInSubject.map((student) => ({
      studentId: student.id,
      studentName: getStudentDisplayName(student),
      rollNumber: student.rollNumber || "",
      theoryMarks: null,
      practicalMarks: null,
      marksObtained: null,
    }));

    setForm((prev) => ({ ...prev, studentMarks: cleared }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.subjectId, form.marksheetType, form.monthType, studentsEnrolledInSubject, getSelectionKey]);

  // Autofill marks if matching marks entries exist for this selection (exam + month + class + session + subject).
  useEffect(() => {
    if (!open) return;
    if (!form.subjectId) return;
    if (studentsEnrolledInSubject.length === 0) return;

    const selectedSubjectLocal = allClassSubjects.find((s) => s.id === form.subjectId);
    if (!selectedSubjectLocal?.subjectCode) return;

    const selectionKey = getSelectionKey();
    const appliedKey = `${selectionKey}|${marksEntries?.length ?? 0}`;
    if (lastAutofillAppliedKeyRef.current === appliedKey) return;

    const matchingEntries = (marksEntries || []).filter(
      (entry) =>
        entry.marksheetType === effectiveMarksheetType &&
        entry.monthType === form.monthType &&
        entry.classId === form.classId &&
        entry.sessionId === form.sessionId
    );

    const marksMap = new Map<number, { theory: number; practical: number }>();
    matchingEntries.forEach((entry) => {
      entry.subjectMarksDtoList?.forEach((subjectMark) => {
        if (subjectMark.subjectCode === selectedSubjectLocal.subjectCode) {
          // If multiple entries exist, first match wins (consistent + stable)
          if (!marksMap.has(subjectMark.studentId)) {
            marksMap.set(subjectMark.studentId, {
              theory: subjectMark.theory,
              practical: subjectMark.practical,
            });
          }
        }
      });
    });

    // Apply marks from API, but preserve the user's current typing if they already changed a field.
    setForm((prev) => {
      const nextMarks = studentsEnrolledInSubject.map((student) => {
        const existingRow = prev.studentMarks.find((sm) => sm.studentId === student.id);
        const apiMarks = marksMap.get(student.id);

        // If the user already typed something for this student in current selection, keep it.
        const userTouched =
          existingRow &&
          (existingRow.marksObtained !== null ||
            existingRow.theoryMarks !== null ||
            existingRow.practicalMarks !== null);

        if (userTouched) {
          return {
            ...existingRow,
            studentName: getStudentDisplayName(student),
            rollNumber: student.rollNumber || "",
          };
        }

        if (form.marksheetType === "MONTHLY") {
          const marksObtained =
            apiMarks !== undefined && apiMarks.theory >= 0 ? apiMarks.theory : null;
          return {
            studentId: student.id,
            studentName: getStudentDisplayName(student),
            rollNumber: student.rollNumber || "",
            theoryMarks: null,
            practicalMarks: null,
            marksObtained,
          };
        }

        const theoryMarks =
          apiMarks !== undefined && apiMarks.theory >= 0 ? apiMarks.theory : null;
        const practicalMarks =
          apiMarks !== undefined && apiMarks.practical >= 0 ? apiMarks.practical : null;

        return {
          studentId: student.id,
          studentName: getStudentDisplayName(student),
          rollNumber: student.rollNumber || "",
          theoryMarks,
          practicalMarks,
          marksObtained: null,
        };
      });

      return { ...prev, studentMarks: nextMarks };
    });

    lastAutofillAppliedKeyRef.current = appliedKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    form.subjectId,
    form.marksheetType,
    form.monthType,
    form.classId,
    form.sessionId,
    studentsEnrolledInSubject,
    marksEntries,
    allClassSubjects,
    getSelectionKey,
  ]);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    
    // Auto-set monthType to "NA" when non-MONTHLY marksheet types are selected
    let updatedMonthType: string | undefined = undefined;
    if (name === "marksheetType") {
      if (shouldUseNAForMonthType(value)) {
        updatedMonthType = "NA";
      } else if (value === "MONTHLY" && form.monthType === "NA") {
        // Reset to default if switching from NA marksheet type to MONTHLY
        updatedMonthType = "JANUARY";
      }
    }
    
    setForm((f) => ({
      ...f,
      [name!]: name === "subjectId" ? Number(value) : value,
      ...(updatedMonthType !== undefined ? { monthType: updatedMonthType } : {}),
    }));
  };

  const handleStudentMarkChange = (
    idx: number,
    field: "theoryMarks" | "practicalMarks" | "marksObtained",
    value: string | number
  ) => {
    const studentId = form.studentMarks[idx]?.studentId;
    if (studentId && lockedStudentIds.has(studentId)) {
      return; // read-only once saved
    }
    // Keep value as string during typing to allow partial input
    let processedValue: string | number | null;
    
    if (value === '' || value === null || value === undefined) {
      processedValue = null;
    } else {
      // Preserve string value during typing, but allow numbers too
      processedValue = typeof value === 'string' ? value : String(value);
    }
    
    setForm((f) => ({
      ...f,
      studentMarks: f.studentMarks.map((sm, i) =>
        i === idx ? { ...sm, [field]: processedValue } : sm
      ),
    }));

    // Validate marks (only validate if there's a value)
    if (processedValue !== null && processedValue !== undefined && processedValue !== '') {
      const numericValue = Number(processedValue);
      
      // Only validate if it's a valid number
      if (!isNaN(numericValue)) {
        const selectedSubject = allClassSubjects.find(s => s.id === form.subjectId);
        const subjectName = selectedSubject?.name || '';
        const maxMarksConfig = getCustomMaxMarks(subjectName, form.marksheetType);
        
        let maxMarks = 0;
        if (form.marksheetType === "MONTHLY") {
          maxMarks = 20;
        } else {
          maxMarks = field === "theoryMarks" ? maxMarksConfig.theory : maxMarksConfig.practical;
        }
        
        const errorKey = `${field}_${idx}`;
        
        if (numericValue > maxMarks) {
          setFormErrors(prev => ({
            ...prev,
            [errorKey]: t("marks_cannot_exceed").replace('{{maxMarks}}', maxMarks.toString())
          }));
        } else if (numericValue < 0) {
          setFormErrors(prev => ({
            ...prev,
            [errorKey]: t("marks_cannot_be_negative")
          }));
        } else {
          setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[errorKey];
            return newErrors;
          });
        }
      }
    } else {
      const errorKey = `${field}_${idx}`;
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!form.subjectId) {
      showErrorToast(t("please_select_subject"));
      return;
    }

    if (form.studentMarks.length === 0) {
      showErrorToast(t("no_students_enrolled_in_subject"));
      return;
    }

    // Validate marks
    let hasErrors = false;
    const newErrors: { [key: string]: string } = {};
    
    form.studentMarks.forEach((studentMark, idx) => {
      const selectedSubject = allClassSubjects.find(s => s.id === form.subjectId);
      const subjectName = selectedSubject?.name || '';
      const maxMarksConfig = getCustomMaxMarks(subjectName, effectiveMarksheetType);
      
      if (effectiveMarksheetType === "MONTHLY") {
        const marksObtained = studentMark.marksObtained === null || studentMark.marksObtained === '' ? 
          null : Number(studentMark.marksObtained);
        if (marksObtained !== null && marksObtained > 20) {
          newErrors[`marksObtained_${idx}`] = t("marks_cannot_exceed").replace('{{maxMarks}}', '20');
          hasErrors = true;
        }
      } else {
        const theoryMarks = studentMark.theoryMarks === null || studentMark.theoryMarks === '' ? 
          null : Number(studentMark.theoryMarks);
        const practicalMarks = studentMark.practicalMarks === null || studentMark.practicalMarks === '' ? 
          null : Number(studentMark.practicalMarks);
        
        if (theoryMarks !== null && theoryMarks > maxMarksConfig.theory) {
          newErrors[`theoryMarks_${idx}`] = t("theory_marks_cannot_exceed").replace('{{maxMarks}}', maxMarksConfig.theory.toString());
          hasErrors = true;
        }
        if (practicalMarks !== null && practicalMarks > maxMarksConfig.practical) {
          newErrors[`practicalMarks_${idx}`] = t("practical_marks_cannot_exceed").replace('{{maxMarks}}', maxMarksConfig.practical.toString());
          hasErrors = true;
        }
      }
    });
    
    if (hasErrors) {
      setFormErrors(newErrors);
      return;
    }

    // Get selected subject to get subjectCode
    const selectedSubject = allClassSubjects.find(s => s.id === form.subjectId);
    if (!selectedSubject) {
      showErrorToast(t("please_select_subject"));
      return;
    }

    // Prepare data for submission - format as subjectMarksDtoList
    // Only include students who have at least one mark entered
    const subjectMarksDtoList = form.studentMarks
      .map((studentMark) => {
        if (effectiveMarksheetType === "MONTHLY") {
          // For monthly, theory field contains the marks obtained
          const marksObtained = studentMark.marksObtained === null || studentMark.marksObtained === '' 
            ? null 
            : Number(studentMark.marksObtained);
          
          // Skip if no marks entered
          if (marksObtained === null) return null;
          
          return {
            studentId: studentMark.studentId,
            subjectCode: selectedSubject.subjectCode,
            theory: marksObtained,
            practical: 0, // Monthly marksheets don't have practical
          };
        } else {
          // For non-monthly, use theory and practical marks
          const theoryMarks = studentMark.theoryMarks === null || studentMark.theoryMarks === '' 
            ? null 
            : Number(studentMark.theoryMarks);
          const practicalMarks = studentMark.practicalMarks === null || studentMark.practicalMarks === '' 
            ? null 
            : Number(studentMark.practicalMarks);
          
          // Skip if both marks are empty
          if (theoryMarks === null && practicalMarks === null) return null;
          
          return {
            studentId: studentMark.studentId,
            subjectCode: selectedSubject.subjectCode,
            theory: theoryMarks || 0,
            practical: practicalMarks || 0,
          };
        }
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    // Check if there are any marks to submit
    if (subjectMarksDtoList.length === 0) {
      showErrorToast(t("no_marks_to_submit"));
      return;
    }

    // Submit data in the new format
    const submissionData: MarksEntryFormData = {
      marksheetType: effectiveMarksheetType,
      monthType: form.monthType,
      classId: form.classId,
      sessionId: form.sessionId,
      subjectId: form.subjectId,
      studentMarks: form.studentMarks,
      subjectMarksDtoList: subjectMarksDtoList,
    };
    
    onSubmit(submissionData);
  };

  const handleModalClose = () => {
    setFormErrors({});
    handleClose();
  };

  // Helper function to check if subject needs custom max marks
  const getCustomMaxMarks = (subjectName: string, marksheetType: string) => {
    if (marksheetType === "MONTHLY") {
      return { theory: 20, practical: 0 };
    }
    
    const normalizedSubjectName = subjectName.toLowerCase().trim();
    const isVocationalSubject = vocationalSubjects?.has(normalizedSubjectName) ?? false;
    
    // Special case: 9th class with ANNUAL marksheet type
    const is9thClass = baseClassName.toLowerCase().includes("9th");
    const isAnnual = marksheetType === "ANNUAL" || marksheetType === "FINAL";
    
    if (is9thClass && isAnnual) {
      if (isVocationalSubject) {
        return { theory: 40, practical: 60 };
      } else {
        return { theory: 75, practical: 15 };
      }
    }
    
    // Default behavior for other cases
    if (isVocationalSubject) {
      return { theory: 40, practical: 60 };
    }
    
    return { theory: 75, practical: 25 };
  };

  const getFilteredMarksheetOptions = (className: string) => {
    const isBoardClass =
      className.includes("10th") || className.includes("12th");

    return marksheetTypeOptions.filter((opt) => {
      if (isBoardClass) {
        return opt.value !== "ANNUAL";
      } else {
        return opt.value !== "BOARD" && opt.value !== "PRE_BOARD";
      }
    });
  };

  const selectedSubject = allClassSubjects.find(s => s.id === form.subjectId);
  const maxMarksConfig = selectedSubject ? getCustomMaxMarks(selectedSubject.name, effectiveMarksheetType) : { theory: 75, practical: 25 };

  // Lock already-saved marks so user can only view them (prevents duplicate/overwrite entries).
  const lockedStudentIds = useMemo(() => {
    if (!open) return new Set<number>();
    if (!selectedSubject?.subjectCode) return new Set<number>();

    const matchingEntries = (marksEntries || []).filter(
      (entry) =>
        entry.marksheetType === effectiveMarksheetType &&
        entry.monthType === form.monthType &&
        entry.classId === form.classId &&
        entry.sessionId === form.sessionId
    );

    const locked = new Set<number>();
    matchingEntries.forEach((entry) => {
      entry.subjectMarksDtoList?.forEach((subjectMark) => {
        if (subjectMark.subjectCode === selectedSubject.subjectCode) {
          locked.add(subjectMark.studentId);
        }
      });
    });

    return locked;
  }, [
    open,
    marksEntries,
    selectedSubject?.subjectCode,
    effectiveMarksheetType,
    form.monthType,
    form.classId,
    form.sessionId,
  ]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleModalClose();
        }
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <DialogTitle
        sx={{
          background: '#3092e3',
          color: 'white',
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 3,
          px: 4,
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {t("add_marks_entry")}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {t("enter_marks_for_students_by_subject")}
          </Typography>
        </Box>
        <IconButton 
          onClick={handleModalClose} 
          size="small"
          sx={{ 
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        <DialogContent sx={{ p: 4, bgcolor: '#f8fafc', flex: 1, overflow: 'auto' }}>
          {/* Class and Session Information Section */}
          <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <SchoolIcon sx={{ color: "#3092e3" }} />
                <Typography variant="h6" fontWeight="bold" sx={{ color: "#3092e3" }}>
                  {t("class_and_session")}
                </Typography>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("class")}
                    value={className}
                    fullWidth
                    InputProps={{ 
                      readOnly: true,
                      startAdornment: <SchoolIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#f5f5f5'
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("session_year")}
                    value={sessionYear}
                    fullWidth
                    InputProps={{ 
                      readOnly: true,
                      startAdornment: <SchoolIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#f5f5f5'
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Marksheet Details Section */}
          <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <AssessmentIcon sx={{ color: "#3092e3" }} />
                <Typography variant="h6" fontWeight="bold" sx={{ color: "#3092e3" }}>
                  {t("marksheet_details")}
                </Typography>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>
                      {t("marksheet_type")}
                    </InputLabel>
                    <Select
                      name="marksheetType"
                      value={form.marksheetType}
                      onChange={(e) => handleChange(e as any)}
                      required
                      label={t("marksheet_type")}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    >
                      {getFilteredMarksheetOptions(className).map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Typography>{opt.label}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Month Type field - only show for MONTHLY marksheet type */}
                {form.marksheetType === "MONTHLY" && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>
                        {t("month")}
                      </InputLabel>
                      <Select
                        name="monthType"
                        value={form.monthType || "JANUARY"}
                        onChange={(e) => handleChange(e as any)}
                        required
                        label={t("month")}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      >
                        {monthTypeOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            <Typography>{opt.label}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Subject Selection Section */}
          <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <SubjectIcon sx={{ color: "#3092e3" }} />
                <Typography variant="h6" fontWeight="bold" sx={{ color: "#3092e3" }}>
                  {t("select_subject")}
                </Typography>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>
                      {t("subject")}
                    </InputLabel>
                    <Select
                      name="subjectId"
                      value={form.subjectId || ''}
                      onChange={(e) => handleChange(e as any)}
                      required
                      label={t("subject")}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            zIndex: 9999,
                          },
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      }}
                    >
                      {allClassSubjects.map((subject) => (
                        <MenuItem key={subject.id} value={subject.id}>
                          <Typography>{subject.subjectCode} - {subject.name}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("subject_type")}
                    value={(() => {
                      if (!form.subjectId) return '';
                      const selectedSubject = allClassSubjects.find(s => s.id === form.subjectId);
                      if (!selectedSubject) return '';
                      const normalizedSubjectName = selectedSubject.name.toLowerCase().trim();
                      const isVocational = vocationalSubjects?.has(normalizedSubjectName) ?? false;
                      return isVocational ? t("vocational") : t("other");
                    })()}
                    fullWidth
                    InputProps={{ 
                      readOnly: true,
                      startAdornment: <SubjectIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#f5f5f5'
                      }
                    }}
                    placeholder={t("select_subject_first")}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Students and Marks Section */}
          {!form.subjectId ? (
            <Card sx={{ mt: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
              <CardContent sx={{ p: 3 }}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    bgcolor: '#f8f9fa',
                    borderRadius: 2,
                    border: '2px dashed #dee2e6'
                  }}
                >
                  <SubjectIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t("first_select_subject_to_see_students_list")}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          ) : studentsEnrolledInSubject.length === 0 ? (
            <Card sx={{ mt: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
              <CardContent sx={{ p: 3 }}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    bgcolor: '#f8f9fa',
                    borderRadius: 2,
                    border: '2px dashed #dee2e6'
                  }}
                >
                  <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t("no_student_enrolled_in_this_subject")}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ mt: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <PersonIcon sx={{ color: "#3092e3" }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: "#3092e3" }}>
                    {t("students_and_marks")} ({studentsEnrolledInSubject.length} {t("students")})
                  </Typography>
                </Box>
                
                {/* Max Marks Header Row - Show above marks fields only, aligned with marks columns */}
                {form.marksheetType !== "MONTHLY" && (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                    mb={1}
                    p={1.5}
                    sx={{
                      bgcolor: '#f8fafc',
                      borderRadius: 1,
                      border: '1px solid #e2e8f0',
                      width: '500px',
                      ml: 'auto',
                      mr: 0
                    }}
                  >
                    {/* Max Marks Labels - aligned with marks input fields, matching student row flex structure */}
                    <Box sx={{ flex: 1, textAlign: 'center', minWidth: 120 }}>
                      <Typography variant="caption" fontWeight="bold" sx={{ color: "#3092e3" }}>
                        {t("total_marks_theory")}: {maxMarksConfig.theory}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, textAlign: 'center', minWidth: 120 }}>
                      <Typography variant="caption" fontWeight="bold" sx={{ color: "#3092e3" }}>
                        {t("total_marks_practical")}: {maxMarksConfig.practical}
                      </Typography>
                    </Box>
                  </Box>
                )}
                
                {form.studentMarks.map((sm, idx) => (
                  (() => {
                    const isLocked = lockedStudentIds.has(sm.studentId);
                    return (
                  <Box
                    key={sm.studentId}
                    display="flex"
                    alignItems="center"
                    gap={2}
                    mt={2}
                    p={2}
                    sx={{
                      bgcolor: '#ffffff',
                      borderRadius: 2,
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <Avatar sx={{ width: 40, height: 40, bgcolor: '#0168EA' }}>
                      {sm.studentName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 2, minWidth: 200 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {sm.studentName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("roll_number")}: {sm.rollNumber}
                      </Typography>
                    </Box>
                    
                    {form.marksheetType === "MONTHLY" ? (
                      // Monthly format - simple marks entry
                      <>
                        <TextField
                          label={t("total_marks")}
                          type="number"
                          value={20}
                          sx={{ flex: 1 }}
                          inputProps={{ min: 0, max: 20, readOnly: true }}
                        />
                        <TextField
                          label={t("marks_obtained")}
                          type="number"
                          value={sm.marksObtained === null || sm.marksObtained === '' ? '' : sm.marksObtained}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleStudentMarkChange(idx, "marksObtained", value);
                          }}
                          sx={{ flex: 2 }}
                          inputProps={{
                            min: 0,
                            max: 20,
                            step: 1,
                            readOnly: isLocked,
                          }}
                          disabled={isLocked}
                          error={!!formErrors[`marksObtained_${idx}`]}
                          helperText={formErrors[`marksObtained_${idx}`] || ''}
                          placeholder={t("enter_marks_optional")}
                        />
                      </>
                    ) : (
                      // Non-Monthly format - Theory/Practical marks entry
                      <>
                        <TextField
                          label={t("theory_marks_obtained")}
                          type="number"
                          value={sm.theoryMarks === null || sm.theoryMarks === '' ? '' : sm.theoryMarks}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleStudentMarkChange(idx, "theoryMarks", value);
                          }}
                          sx={{ flex: 1 }}
                          inputProps={{ min: 0, max: maxMarksConfig.theory, readOnly: isLocked }}
                          disabled={isLocked}
                          error={!!formErrors[`theoryMarks_${idx}`]}
                          helperText={formErrors[`theoryMarks_${idx}`] || ''}
                          placeholder={t("enter_theory_marks_optional")}
                        />
                        <TextField
                          label={t("practical_marks_obtained")}
                          type="number"
                          value={sm.practicalMarks === null || sm.practicalMarks === '' ? '' : sm.practicalMarks}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleStudentMarkChange(idx, "practicalMarks", value);
                          }}
                          sx={{ flex: 1 }}
                          inputProps={{ min: 0, max: maxMarksConfig.practical, readOnly: isLocked }}
                          disabled={isLocked}
                          error={!!formErrors[`practicalMarks_${idx}`]}
                          helperText={formErrors[`practicalMarks_${idx}`] || ''}
                          placeholder={t("enter_practical_marks_optional")}
                        />
                      </>
                    )}
                  </Box>
                    );
                  })()
                ))}
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            {onViewMarksEntries && (
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#3092e3",
                  "&:hover": { backgroundColor: "#2573c1" },
                  mr: 2,
                }}
                onClick={() => {
                  handleModalClose();
                  onViewMarksEntries();
                }}
                startIcon={<VisibilityIcon />}
              >
                {t("view_marks_entries")}
              </Button>
            )}
          </Box>
          <Box>
            <Button 
              onClick={handleModalClose} 
              variant="outlined"
              sx={{
                minWidth: 100,
                borderColor: '#f44336',
                color: '#f44336',
                '&:hover': {
                  borderColor: '#d32f2f',
                  color: '#d32f2f',
                  backgroundColor: 'rgba(244, 67, 54, 0.04)',
                }
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!form.subjectId || form.studentMarks.length === 0}
              sx={{
                minWidth: 100,
                ml: 2,
                backgroundColor: '#3092e3',
                '&:hover': {
                  backgroundColor: 'primary.main',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#b2ebff',
                  color: '#fff',
                }
              }}
            >
              {t("add")}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MarksEntryFormModal;

